// @flow
import request from 'superagent'
import { getCurrentTimestamp } from 'er2_web_utils'
import { setMapExtent } from 'catena/er2_map_userlayers/js/actions/map'
import * as types from '../constants/action_types'
import * as wsTypes from '../modules/websockets/server/constants/action_types'
import { updateIterationOrder } from './ficus_config'
import { removeMapLocations } from './analysis_panel'
import { collaborationIsEnabled } from './collaboration'
import { setErrorMessage } from './error'
import type { FicusDataType } from '../reducers/select_framework_bar'

export const clearFramework = () => ({ type: types.CLEAR_FRAMEWORK })
const receiveFicusData = (ficusNode: string, ficusData: FicusDataType, mode: ?string) => ({ type: types.RECEIVE_FICUS_DATA, ficusNode, ficusData, mode })
export const setFicusInfoUrl = (frameworkInfoUrl: string) => ({ type: types.SET_FICUS_FRAMEWORK_INFO_URL, frameworkInfoUrl })

const setFetchingFicusData = (fetching: boolean, exception?: { message: string, traceback: string }) =>
    ({ type: types.SET_FETCHING_FICUS_DATA, fetching, exception })

export const handledExtent = () => ({ type: types.EXTENT_HANDLED })
export const handledSetAnalyzeMode = () => ({ type: types.HANDLED_SET_ANALYZE_MODE })
export const handledSetFramework = () => ({ type: types.HANDLED_SET_FRAMEWORK })

const sendFicusNodeToSession = (ficusNode: string, getState: Function, emit: Function) => {
    const state = getState()
    const payload = {
        applicationId: state.meta.name,
        timestamp: getCurrentTimestamp(),
        operation: {
            name: types.OT_SET_FRAMEWORK,
            originator: state.token.value,
            params: { ficusNode },
        },
    }
    if (collaborationIsEnabled(state) && !state.collaborationSettings.readOnly && state.collaboration.sharedState.propagateSetFramework) {
        console.warn(`sendFicusNodeToSession: emitting ${wsTypes.WS_CLIENT_ACTION}`)
        payload.roomId = state.collaboration.sharedState.token
        emit(wsTypes.WS_CLIENT_ACTION, payload)
    }
    handledSetFramework()
}

const sendAnalyzeModeToSession = (analyzeMode: string, getState: Function, emit: Function) => {
    const state = getState()
    const payload = {
        applicationId: state.meta.name,
        timestamp: getCurrentTimestamp(),
        operation: {
            name: types.OT_SET_ANALYZE_MODE,
            originator: state.token.value,
            params: { analyzeMode },
        },
    }
    if (collaborationIsEnabled(state) && !state.collaborationSettings.readOnly && state.collaboration.sharedState.propagateSetAnalysisMode) {
        console.warn(`sendAnalyzeModeToSession: emitting ${wsTypes.WS_CLIENT_ACTION}`)
        payload.roomId = state.collaboration.sharedState.token
        emit(wsTypes.WS_CLIENT_ACTION, payload)
    }
    handledSetAnalyzeMode()
}

const loadFicusData = (ficusNode: string, analysisMode: string, state, dispatch: Function, loadFinished: Function) => {
    // Look for cached framework
    const key = `${ficusNode}_${analysisMode}`
    const ficusCache = state.selectFrameworkBar.ficusDataCache
    if (ficusCache[key]) {
        dispatch(receiveFicusData(ficusNode, ficusCache[key], analysisMode))
        loadFinished(ficusCache[key])
    } else {
        dispatch(setFetchingFicusData(true))
        request.get(`/er2_ficus/api/v1/ficus/theme/${ficusNode}/?mode=${analysisMode}`)
            .then((ret) => {
                if (ret.body.error) {
                    dispatch(setErrorMessage(ret.body.error))
                } else {
                    // Strip extra extent data from mapfiles
                    const ficusData = ret.body.state
                    dispatch(setFetchingFicusData(false))
                    dispatch(receiveFicusData(ficusNode, ficusData, analysisMode))
                    if (!state.selectFrameworkBar.extentHandled && ficusData.themeType === 'raster' && ficusData.themes[0].mapfiles) {
                        dispatch(setMapExtent(ficusData.themes[0].mapfiles[0].extent.bbox_3857))
                        dispatch(handledExtent())
                    }
                    loadFinished(ficusData)
                }
            }, (ret) => {
                console.error(ret.message)
                dispatch(setFetchingFicusData(false))
            })
    }
}

export const setAnalyzeMode = (mode: string) => (dispatch: Function, getState: Function, { emit }) => {
    const state = getState()
    if (mode !== state.analyzeFrameworkBar.mode) {
        loadFicusData(state.selectFrameworkBar.ficusNode, mode, state, dispatch, () => {
            // Trigger a sort on cell data to calculate iterationOrder
            // TODO: make this into its own function that will sort the values or grayswans
            dispatch(updateIterationOrder(state.analyzeFrameworkBar.iterationType))
            dispatch({ type: types.SET_ANALYZE_MODE, mode })
            sendAnalyzeModeToSession(mode, getState, emit)
        })
    }
}

export const setFicusData = (ficusNode: string, mode: ?string) => (dispatch: Function, getState: Function, { emit }) => {
    const state = getState()
    const defaultMode = mode || '_quickOverview'

    dispatch(removeMapLocations())
    document.title = `${ficusNode} <FICUS>`

    if (ficusNode) {
        loadFicusData(ficusNode, defaultMode, state, dispatch, (ficusData) => {
            // Trigger a sort on cell data to calculate iterationOrder
            // TODO: make this into its own function that will sort the values or grayswans
            dispatch(updateIterationOrder(state.analyzeFrameworkBar.iterationType))
            sendFicusNodeToSession(ficusNode, getState, emit)
        })
    } else {
        // Assume request to clear out exiting data
        dispatch(receiveFicusData(ficusNode, null, null))
    }
}

const setFetchingFrameworkInfo = (fetching: boolean, exception?: { message: string, traceback: string }) =>
    ({ type: types.SET_FETCHING_FRAMEWORK_INFO, fetching, exception })

const receiveFicusFrameworkInfo = (f: { frameworkInfo: string, framworkInfoUrl: string }) => ({ type: types.RECEIVE_FICUS_FRAMEWORK_INFO, ...f })

export const getFicusInfo = (ficusNode: string) => (dispatch: Function, getState: Function) => {
    dispatch(setFetchingFrameworkInfo(true))
    request.post(`/er2_ficus/getFrameworkInfo/${ficusNode}/`)
        .send()
        .then((ret) => {
            dispatch(setFetchingFrameworkInfo(false))
            dispatch(receiveFicusFrameworkInfo(ret.body))
        }, (ret) => {
            dispatch(setFetchingFrameworkInfo(false))
            console.error(ret)
        })
}
